/**
 * useFilePreview — turns a local File (image or PDF) into a thumbnail URL.
 *
 *   • image/* → revocable object URL.
 *   • application/pdf → first page rendered to a PNG data URL (via pdf.js).
 *   • Anything else → null.
 *
 * Result is cleaned up when the component unmounts.
 */
import { useEffect, useState } from 'react';

let pdfjsPromise = null;
function loadPdfjs() {
  if (pdfjsPromise) return pdfjsPromise;
  pdfjsPromise = import('pdfjs-dist').then(pdfjs => {
    pdfjs.GlobalWorkerOptions.workerSrc =
      'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/4.10.38/pdf.worker.min.mjs';
    return pdfjs;
  });
  return pdfjsPromise;
}

async function fileToThumbnail(file) {
  if (!file) return null;
  if (file.type.startsWith('image/')) {
    return URL.createObjectURL(file);
  }
  if (file.type === 'application/pdf') {
    const pdfjs = await loadPdfjs();
    const buf = await file.arrayBuffer();
    const pdf = await pdfjs.getDocument({ data: buf }).promise;
    const page = await pdf.getPage(1);
    const baseViewport = page.getViewport({ scale: 1 });
    const targetWidth = 700;
    const scale = targetWidth / baseViewport.width;
    const viewport = page.getViewport({ scale });
    const canvas = document.createElement('canvas');
    canvas.width = viewport.width;
    canvas.height = viewport.height;
    await page.render({
      canvasContext: canvas.getContext('2d'),
      viewport,
    }).promise;
    return canvas.toDataURL('image/png');
  }
  return null;
}

export function useFilePreview(file) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let cancelled = false;
    let revoke = null;
    if (!file) { setUrl(null); return undefined; }
    fileToThumbnail(file).then(u => {
      if (cancelled) {
        if (u && u.startsWith('blob:')) URL.revokeObjectURL(u);
        return;
      }
      setUrl(u);
      if (u && u.startsWith('blob:')) revoke = u;
    }).catch(() => { if (!cancelled) setUrl(null); });
    return () => {
      cancelled = true;
      if (revoke) URL.revokeObjectURL(revoke);
    };
  }, [file]);
  return url;
}
